import { Component } from '@angular/core';
import { CarrierId, SCV2_CARRIERS, ShippingConfigV2Service, ShippingOrderV2 } from '../../shipping-config-v2.service';

interface RateOption {
  id: string;
  carrier: CarrierId;
  service: string;
  eta: string;
  price: number;
}

@Component({
  selector: 'app-shipping-labels-dashboard-v2',
  templateUrl: './shipping-labels-dashboard-v2.component.html',
  styleUrls: ['./shipping-labels-dashboard-v2.component.scss'],
})
export class ShippingLabelsDashboardV2Component {
  carriers = SCV2_CARRIERS;
  readonly steps = ['Label purchased', 'In transit', 'Out for delivery', 'Delivered'];

  filter: 'all' | 'unfulfilled' | 'fulfilled' | 'delivered' = 'all';
  filterChips: ('all' | 'unfulfilled' | 'fulfilled' | 'delivered')[] = ['all', 'unfulfilled', 'fulfilled', 'delivered'];

  openOrder: ShippingOrderV2 | null = null;
  printingOrder: ShippingOrderV2 | null = null;

  // create-label modal state
  selectedRate: string | null = null;
  buying = false;
  manualCarrier: CarrierId = 'usps';
  manualTracking = '';

  constructor(public service: ShippingConfigV2Service) {}

  get orders(): ShippingOrderV2[] {
    return this.service.orders;
  }

  get connections() {
    return this.service.connections;
  }

  get anyCarrier(): boolean {
    return !!(this.connections.ups || this.connections.usps);
  }

  get counts() {
    return {
      unfulfilled: this.orders.filter((o) => o.step === -1).length,
      fulfilled: this.orders.filter((o) => o.step >= 0 && o.step < 3).length,
      delivered: this.orders.filter((o) => o.step === 3).length,
    };
  }

  get filtered(): ShippingOrderV2[] {
    return this.orders.filter((o) => {
      if (this.filter === 'all') return true;
      if (this.filter === 'unfulfilled') return o.step === -1;
      if (this.filter === 'fulfilled') return o.step >= 0 && o.step < 3;
      if (this.filter === 'delivered') return o.step === 3;
      return true;
    });
  }

  statusMeta(step: number): { label: string; tone: string } {
    if (step === -1) return { label: 'Unfulfilled', tone: 'orange' };
    if (step === 3) return { label: 'Delivered', tone: 'green' };
    if (step === 2) return { label: 'Out for delivery', tone: 'orange' };
    return { label: this.steps[step], tone: 'blue' };
  }

  chipLabel(f: string): string {
    return f === 'all' ? 'All orders' : f[0].toUpperCase() + f.slice(1);
  }

  setStatFilter(id: 'unfulfilled' | 'fulfilled' | 'delivered'): void {
    this.filter = this.filter === id ? 'all' : id;
  }

  // ---- modal lifecycle ----
  open(order: ShippingOrderV2): void {
    this.openOrder = order;
    this.selectedRate = null;
    this.buying = false;
    this.manualTracking = '';
    this.manualCarrier = this.manualCarriers[0] || 'usps';
  }

  close(): void {
    this.openOrder = null;
  }

  updateOrder(id: string, patch: Partial<ShippingOrderV2>): void {
    this.service.updateOrder(id, patch);
    if (this.openOrder && this.openOrder.id === id) {
      this.openOrder = { ...this.openOrder, ...patch };
    }
  }

  quickPrint(order: ShippingOrderV2): void {
    this.printingOrder = order;
    setTimeout(() => {
      window.print();
      this.printingOrder = null;
    }, 80);
  }

  // ---- create-label modal computed ----
  get apiCarriers(): CarrierId[] {
    return (Object.keys(this.connections) as CarrierId[]).filter((k) => this.connections[k]?.mode === 'api');
  }

  get manualCarriers(): CarrierId[] {
    return (Object.keys(this.connections) as CarrierId[]).filter((k) => this.connections[k]?.mode === 'manual');
  }

  get rateOptions(): RateOption[] {
    const api = this.apiCarriers;
    const opts: (RateOption | false)[] = [
      api.includes('usps') && { id: 'usps-pm', carrier: 'usps', service: 'USPS Priority Mail', eta: '2–3 business days', price: 7.4 },
      api.includes('usps') && { id: 'usps-fc', carrier: 'usps', service: 'USPS First Class', eta: '3–5 business days', price: 4.95 },
      api.includes('ups') && { id: 'ups-gr', carrier: 'ups', service: 'UPS Ground', eta: '3–5 business days', price: 9.2 },
      api.includes('ups') && { id: 'ups-2d', carrier: 'ups', service: 'UPS 2nd Day Air', eta: '2 business days', price: 18.5 },
    ];
    return opts.filter(Boolean) as RateOption[];
  }

  get cheapestId(): string | null {
    const r = this.rateOptions;
    return r.length ? r.reduce((a, b) => (a.price < b.price ? a : b)).id : null;
  }

  selectedRateOption(): RateOption | undefined {
    return this.rateOptions.find((r) => r.id === this.selectedRate);
  }

  buyLabel(): void {
    const rate = this.selectedRateOption();
    if (!rate || !this.openOrder) return;
    this.buying = true;
    const id = this.openOrder.id;
    setTimeout(() => {
      this.updateOrder(id, {
        step: 0,
        carrierId: rate.carrier,
        service: rate.service,
        price: rate.price,
        tracking: '1Z' + Math.random().toString(36).slice(2, 11).toUpperCase(),
      });
      this.buying = false;
    }, 900);
  }

  fulfillManually(): void {
    if (!this.openOrder) return;
    this.updateOrder(this.openOrder.id, {
      step: 1,
      carrierId: this.manualCarrier,
      service: 'Manual tracking',
      price: null,
      tracking: this.manualTracking || '—',
    });
  }

  voidLabel(): void {
    if (!this.openOrder) return;
    this.updateOrder(this.openOrder.id, { step: -1, carrierId: null, service: null, tracking: null, price: null });
  }

  advance(): void {
    if (!this.openOrder) return;
    this.updateOrder(this.openOrder.id, { step: Math.min(this.openOrder.step + 1, this.steps.length - 1) as any });
  }

  print(): void {
    window.print();
  }
}
